import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, CheckCircle, RefreshCw, AlertTriangle } from "lucide-react";
import { AppLayout } from "../../../components/layout";
import { ContentCard } from "../../../components/ui";
import { inventoryManagementService } from "../../../services/inventoryManagementService";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { cn } from "../../../lib/utils";

export function StockTransferPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [successToast, setSuccessToast] = useState("");

  // Master lists
  const [warehouses, setWarehouses] = useState([]);
  const [products, setProducts] = useState([]);
  const [batches, setBatches] = useState([]);

  // Form Fields
  const [sourceWarehouseId, setSourceWarehouseId] = useState("");
  const [productId, setProductId] = useState("");
  const [batchNumber, setBatchNumber] = useState("");
  const [transferQty, setTransferQty] = useState(0);
  const [destinationWarehouseId, setDestinationWarehouseId] = useState("");
  const [transferDate, setTransferDate] = useState("2026-08-05"); // Default today
  const [remarks, setRemarks] = useState("");

  // Loading sub-dropdown states
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [loadingBatches, setLoadingBatches] = useState(false);

  // Load warehouses on init
  useEffect(() => {
    let active = true;
    setLoading(true);
    inventoryManagementService.getTransferWarehouses()
      .then((res) => {
        if (!active) return;
        setWarehouses(res);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Failed to load warehouses list:", err);
        setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  // When source warehouse changes, query eligible products
  useEffect(() => {
    if (!sourceWarehouseId) {
      setProducts([]);
      setProductId("");
      return;
    }

    let active = true;
    setLoadingProducts(true);
    setProductId("");
    setBatches([]);
    setBatchNumber("");
    setTransferQty(0);

    inventoryManagementService.getTransferProducts(sourceWarehouseId)
      .then((res) => {
        if (!active) return;
        setProducts(res);
        setLoadingProducts(false);
      })
      .catch((err) => {
        console.error("Failed to load transfer products:", err);
        setLoadingProducts(false);
      });

    return () => {
      active = false;
    };
  }, [sourceWarehouseId]);

  // When product changes, query eligible batches
  useEffect(() => {
    if (!sourceWarehouseId || !productId) {
      setBatches([]);
      setBatchNumber("");
      return;
    }

    let active = true;
    setLoadingBatches(true);
    setBatchNumber("");
    setTransferQty(0);

    inventoryManagementService.getTransferBatches(sourceWarehouseId, productId)
      .then((res) => {
        if (!active) return;
        setBatches(res);
        setLoadingBatches(false);
      })
      .catch((err) => {
        console.error("Failed to load transfer batches:", err);
        setLoadingBatches(false);
      });

    return () => {
      active = false;
    };
  }, [sourceWarehouseId, productId]);

  // Currently selected batch properties
  const selectedBatchObj = useMemo(() => {
    return batches.find((b) => b.batchNumber === batchNumber) || null;
  }, [batches, batchNumber]);

  const availableQty = selectedBatchObj ? selectedBatchObj.availableQty : 0;

  // Live remaining quantity calculation
  const remainingQty = useMemo(() => {
    return Math.max(0, availableQty - transferQty);
  }, [availableQty, transferQty]);

  // Validation checks
  const errors = useMemo(() => {
    const errs = {};
    if (!sourceWarehouseId) {
      errs.sourceWarehouseId = t("validation.fieldRequired", {
        field: t("inventory.sourceWarehouse"),
      });
    }
    if (!productId) {
      errs.productId = t("validation.fieldRequired", { field: t("inventory.product") });
    }
    if (!batchNumber) {
      errs.batchNumber = t("validation.fieldRequired", { field: t("inventory.batchNumber") });
    }

    if (transferQty <= 0) {
      errs.transferQty = t("validation.qtyMustBeGreaterThanZero", {
        field: t("formActions.transferQuantity"),
      });
    } else if (transferQty > availableQty) {
      errs.transferQty = t("validation.transferQtyExceedsAvailable");
    }

    if (!destinationWarehouseId) {
      errs.destinationWarehouseId = t("validation.fieldRequired", {
        field: t("inventory.destinationWarehouse"),
      });
    } else if (destinationWarehouseId === sourceWarehouseId) {
      errs.destinationWarehouseId = t("validation.sourceDestMustDiffer");
    }

    if (!transferDate) {
      errs.transferDate = t("validation.fieldRequired", { field: t("dateTime.transferDate") });
    }
    if (remarks.length > 500) {
      errs.remarks = t("validation.remarksTooLong");
    }

    return errs;
  }, [
    sourceWarehouseId,
    productId,
    batchNumber,
    transferQty,
    availableQty,
    destinationWarehouseId,
    transferDate,
    remarks,
    t,
  ]);

  const hasErrors = Object.keys(errors).length > 0;

  const handleCancel = () => {
    navigate(-1);
  };

  const handleSave = (e) => {
    e.preventDefault();
    if (hasErrors) return;

    setSubmitting(true);
    const payload = {
      sourceWarehouseId,
      sourceWarehouseName: warehouses.find((w) => w.id === sourceWarehouseId)?.name,
      productId,
      productName: products.find((p) => p.id === productId)?.name,
      batchNumber,
      availableQty,
      transferQty,
      destinationWarehouseId,
      destinationWarehouseName: warehouses.find((w) => w.id === destinationWarehouseId)?.name,
      transferDate,
      remarks,
    };

    inventoryManagementService.createStockTransfer(payload)
      .then(() => {
        setSuccessToast(t("inventory.stockTransferredSuccess"));
        setSubmitting(false);
        setTimeout(() => {
          setSuccessToast("");
          navigate("/inventory-management/stock-position");
        }, 1500);
      })
      .catch((err) => {
        console.error("Failed to complete stock transfer:", err);
        setSubmitting(false);
      });
  };

  // Resolved lookup descriptions for Summary panel
  const summaryDetails = useMemo(() => {
    const emDash = "—";
    return {
      sourceName: warehouses.find((w) => w.id === sourceWarehouseId)?.name || emDash,
      destName: warehouses.find((w) => w.id === destinationWarehouseId)?.name || emDash,
      productName: products.find((p) => p.id === productId)?.name || emDash,
      batch: batchNumber || emDash,
      avail: selectedBatchObj ? `${availableQty} ${t("uom.nos")}` : emDash,
      transfer: transferQty > 0 ? `${transferQty} ${t("uom.nos")}` : emDash,
      remaining: selectedBatchObj ? `${remainingQty} ${t("uom.nos")}` : emDash,
      date: transferDate
        ? new Date(transferDate).toLocaleDateString("en-IN", {
            day: "numeric",
            month: "short",
            year: "numeric",
          })
        : emDash,
    };
  }, [
    warehouses,
    sourceWarehouseId,
    destinationWarehouseId,
    products,
    productId,
    batchNumber,
    selectedBatchObj,
    availableQty,
    transferQty,
    remainingQty,
    transferDate,
    t,
  ]);

  return (
    <AppLayout
      breadcrumb={[
        { label: t("inventory.operations") },
        {
          label: t("inventory.inventoryManagement"),
          onClick: () => navigate("/inventory-management/stock-position"),
        },
        { label: t("inventory.stockTransfer") },
      ]}
      className="space-y-6"
    >
      {/* Toast Alert Success popup */}
      {successToast && (
        <div className="fixed top-5 right-5 z-50 bg-emerald-600 text-white px-5 py-3 rounded-xl shadow-lg flex items-center gap-2 animate-in fade-in duration-300 font-sans font-bold text-xs">
          <span>✔️</span>
          <span>{successToast}</span>
        </div>
      )}

      {/* Custom Title and Back Nav */}
      <div className="space-y-1 font-sans">
        <button
          onClick={handleCancel}
          className="inline-flex items-center gap-1 text-[var(--color-primary)] text-xs font-bold hover:underline bg-transparent border-0 cursor-pointer focus:outline-none mb-2"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          <span>{t("formActions.backToInventoryManagement")}</span>
        </button>

        <h1 className="text-xl sm:text-2xl font-extrabold text-foreground tracking-tight">
          {t("inventory.stockTransferTitle")}
        </h1>
      </div>

      {loading ? (
        /* Form loading skeleton */
        <ContentCard bodyClassName="p-6 h-[400px] animate-pulse bg-gray-50/20" />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 font-sans">
          {/* Card 1: Stock Transfer Form */}
          <div className="lg:col-span-8">
            <ContentCard title={t("inventory.stockTransferDetails")} bodyClassName="p-6">
              <form onSubmit={handleSave} className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-5">
                  {/* Source Warehouse Selector */}
                  <div className="space-y-1.5">
                    <label className="text-[11px] font-bold uppercase tracking-wider text-[var(--color-text-secondary)]">
                      {t("inventory.sourceWarehouse")} <span className="text-rose-500">*</span>
                    </label>
                    <Select
                      value={sourceWarehouseId}
                      onValueChange={setSourceWarehouseId}
                    >
                      <SelectTrigger className="h-10 rounded-xl border border-[var(--color-text-border)] bg-white text-xs font-semibold focus:ring-[var(--color-accent)] w-full">
                        <SelectValue placeholder={t("inventory.selectWarehouse")} />
                      </SelectTrigger>
                      <SelectContent>
                        {warehouses.map((w) => (
                          <SelectItem key={w.id} value={w.id}>
                            {w.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Product Selector */}
                  <div className="space-y-1.5">
                    <label className="text-[11px] font-bold uppercase tracking-wider text-[var(--color-text-secondary)] flex items-center gap-1">
                      <span>{t("inventory.product")}</span>
                      <span className="text-rose-500">*</span>
                      {loadingProducts && <RefreshCw className="h-3 w-3 animate-spin text-[var(--color-primary)]" />}
                    </label>
                    <Select
                      value={productId}
                      onValueChange={setProductId}
                      disabled={!sourceWarehouseId || loadingProducts}
                    >
                      <SelectTrigger className="h-10 rounded-xl border border-[var(--color-text-border)] bg-white text-xs font-semibold focus:ring-[var(--color-accent)] w-full disabled:opacity-50">
                        <SelectValue
                          placeholder={
                            !sourceWarehouseId
                              ? t("inventory.selectSourceWarehouseFirst")
                              : t("inventory.selectProduct")
                          }
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {products.map((p) => (
                          <SelectItem key={p.id} value={p.id}>
                            {p.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {sourceWarehouseId && !loadingProducts && products.length === 0 && (
                      <p className="text-[11px] text-rose-500 font-bold mt-1">
                        ⚠️ {t("inventory.noInventoryAvailable")}
                      </p>
                    )}
                  </div>

                  {/* Batch Number Selector */}
                  <div className="space-y-1.5">
                    <label className="text-[11px] font-bold uppercase tracking-wider text-[var(--color-text-secondary)] flex items-center gap-1">
                      <span>{t("inventory.batchNumber")}</span>
                      <span className="text-rose-500">*</span>
                      {loadingBatches && <RefreshCw className="h-3 w-3 animate-spin text-[var(--color-primary)]" />}
                    </label>
                    <Select
                      value={batchNumber}
                      onValueChange={setBatchNumber}
                      disabled={!productId || loadingBatches}
                    >
                      <SelectTrigger className="h-10 rounded-xl border border-[var(--color-text-border)] bg-white text-xs font-semibold focus:ring-[var(--color-accent)] w-full disabled:opacity-50">
                        <SelectValue
                          placeholder={
                            !productId
                              ? t("inventory.selectProductFirst")
                              : t("inventory.selectBatch")
                          }
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {batches.map((b) => (
                          <SelectItem key={b.batchNumber} value={b.batchNumber}>
                            {b.batchNumber}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <span className="text-[10px] text-gray-400 block mt-1 leading-tight">
                      {t("inventory.batchScopeHint")}
                    </span>
                    {productId && !loadingBatches && batches.length === 0 && (
                      <p className="text-[11px] text-rose-500 font-bold mt-1">
                        ⚠️ {t("inventory.noTransferableBatches")}
                      </p>
                    )}
                  </div>

                  {/* Available Quantity */}
                  <div className="space-y-1.5">
                    <label className="text-[11px] font-bold uppercase tracking-wider text-[var(--color-text-secondary)]">
                      {t("inventory.availableQty")}
                    </label>
                    <div className="h-10 px-3 bg-gray-50 border border-[var(--color-text-border)] rounded-xl flex items-center text-xs font-bold text-foreground font-mono">
                      {selectedBatchObj ? availableQty : "—"}
                    </div>
                    <span className="text-[10px] text-gray-400 block mt-1 leading-tight">
                      {t("inventory.scopedNetHelper")}
                    </span>
                  </div>

                  {/* Transfer Quantity */}
                  <div className="space-y-1.5">
                    <label className="text-[11px] font-bold uppercase tracking-wider text-[var(--color-text-secondary)]">
                      {t("formActions.transferQuantity")} <span className="text-rose-500">*</span>
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={transferQty || ""}
                      onChange={(e) => setTransferQty(Math.max(0, Number(e.target.value) || 0))}
                      disabled={!batchNumber}
                      placeholder="0"
                      className="w-full h-10 px-3 rounded-xl border border-[var(--color-text-border)] bg-white text-xs font-semibold focus:border-[var(--color-accent)] focus:outline-none disabled:opacity-50"
                    />
                    {transferQty > availableQty && (
                      <p className="text-[11px] text-rose-500 font-semibold mt-1">
                        {t("validation.transferQtyExceedsAvailable")}
                      </p>
                    )}
                  </div>

                  {/* Destination Warehouse */}
                  <div className="space-y-1.5">
                    <label className="text-[11px] font-bold uppercase tracking-wider text-[var(--color-text-secondary)]">
                      {t("inventory.destinationWarehouse")} <span className="text-rose-500">*</span>
                    </label>
                    <Select
                      value={destinationWarehouseId}
                      onValueChange={setDestinationWarehouseId}
                    >
                      <SelectTrigger className="h-10 rounded-xl border border-[var(--color-text-border)] bg-white text-xs font-semibold focus:ring-[var(--color-accent)] w-full">
                        <SelectValue placeholder={t("inventory.selectWarehouse")} />
                      </SelectTrigger>
                      <SelectContent>
                        {warehouses.map((w) => (
                          <SelectItem key={w.id} value={w.id}>
                            {w.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {destinationWarehouseId && destinationWarehouseId === sourceWarehouseId && (
                      <p className="text-[11px] text-rose-500 font-semibold mt-1">
                        {t("validation.sourceDestMustDiffer")}
                      </p>
                    )}
                  </div>

                  {/* Transfer Date */}
                  <div className="space-y-1.5">
                    <label className="text-[11px] font-bold uppercase tracking-wider text-[var(--color-text-secondary)]">
                      {t("dateTime.transferDate")} <span className="text-rose-500">*</span>
                    </label>
                    <input
                      type="date"
                      value={transferDate}
                      onChange={(e) => setTransferDate(e.target.value)}
                      className="w-full h-10 px-3 rounded-xl border border-[var(--color-text-border)] bg-white text-xs font-semibold focus:border-[var(--color-accent)] focus:outline-none"
                    />
                  </div>

                  {/* Remarks */}
                  <div className="space-y-1.5 sm:col-span-2">
                    <label className="text-[11px] font-bold uppercase tracking-wider text-[var(--color-text-secondary)]">
                      {t("common.remarks")}
                    </label>
                    <textarea
                      value={remarks}
                      onChange={(e) => setRemarks(e.target.value)}
                      placeholder={t("inventory.remarksOptionalPlaceholder")}
                      className="w-full h-20 p-3 rounded-xl border border-[var(--color-text-border)] bg-white text-xs font-semibold focus:border-[var(--color-accent)] focus:outline-none resize-none"
                    />
                  </div>
                </div>

                {/* Form Footer Action Buttons */}
                <div className="flex items-center justify-end gap-3 pt-5 border-t border-[var(--color-text-border)]/50">
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="h-10 px-5 rounded-xl border border-[var(--color-text-border)] text-[var(--color-text-secondary)] font-bold text-xs hover:bg-gray-50 transition cursor-pointer"
                  >
                    {t("formActions.cancel")}
                  </button>
                  <button
                    type="submit"
                    disabled={hasErrors || submitting}
                    className="h-10 px-6 rounded-xl bg-[var(--color-primary)] hover:bg-[var(--color-accent)] text-white font-bold text-xs transition shadow-[0_8px_20px_-8px_rgba(30,51,131,0.5)] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {submitting ? t("inventory.saving") : t("formActions.saveTransfer")}
                  </button>
                </div>
              </form>
            </ContentCard>
          </div>

          {/* Card 2: Transfer Summary (Side Panel) */}
          <div className="lg:col-span-4">
            <ContentCard title={t("formActions.transferSummary")} bodyClassName="p-5 font-sans space-y-4">
              <div className="space-y-3.5 text-xs">
                {/* Source */}
                <div className="flex justify-between border-b border-gray-100 pb-2">
                  <span className="font-semibold text-gray-400 uppercase text-[9px] tracking-wide">
                    {t("inventory.sourceWarehouse")}
                  </span>
                  <span className="font-bold text-foreground text-right truncate max-w-[200px]">
                    {summaryDetails.sourceName}
                  </span>
                </div>

                {/* Destination */}
                <div className="flex justify-between border-b border-gray-100 pb-2">
                  <span className="font-semibold text-gray-400 uppercase text-[9px] tracking-wide">
                    {t("inventory.destinationWarehouse")}
                  </span>
                  <span className={cn(
                    "font-bold text-right truncate max-w-[200px]",
                    errors.destinationWarehouseId && destinationWarehouseId ? "text-rose-600" : "text-foreground"
                  )}>
                    {summaryDetails.destName}
                  </span>
                </div>

                {/* Product */}
                <div className="flex justify-between border-b border-gray-100 pb-2">
                  <span className="font-semibold text-gray-400 uppercase text-[9px] tracking-wide">
                    {t("inventory.product")}
                  </span>
                  <span className="font-bold text-foreground text-right truncate max-w-[200px]">
                    {summaryDetails.productName}
                  </span>
                </div>

                {/* Batch */}
                <div className="flex justify-between border-b border-gray-100 pb-2">
                  <span className="font-semibold text-gray-400 uppercase text-[9px] tracking-wide">
                    {t("inventory.batchNumber")}
                  </span>
                  <span className="font-mono font-bold text-foreground">
                    {summaryDetails.batch}
                  </span>
                </div>

                {/* Available */}
                <div className="flex justify-between border-b border-gray-100 pb-2">
                  <span className="font-semibold text-gray-400 uppercase text-[9px] tracking-wide">
                    {t("inventory.availableQty")}
                  </span>
                  <span className="font-mono font-bold text-foreground">
                    {summaryDetails.avail}
                  </span>
                </div>

                {/* Transfer Qty */}
                <div className="flex justify-between border-b border-gray-100 pb-2">
                  <span className="font-semibold text-gray-400 uppercase text-[9px] tracking-wide">
                    {t("formActions.transferQuantity")}
                  </span>
                  <span className={cn(
                    "font-mono font-bold",
                    errors.transferQty && transferQty > 0 ? "text-rose-600 animate-pulse" : "text-[var(--color-primary)]"
                  )}>
                    {summaryDetails.transfer}
                  </span>
                </div>

                {/* Remaining */}
                <div className="flex justify-between border-b border-gray-100 pb-2">
                  <span className="font-semibold text-gray-400 uppercase text-[9px] tracking-wide">
                    {t("inventory.remainingQty")}
                  </span>
                  <span className="font-mono font-extrabold text-emerald-600">
                    {summaryDetails.remaining}
                  </span>
                </div>

                {/* Date */}
                <div className="flex justify-between pb-1">
                  <span className="font-semibold text-gray-400 uppercase text-[9px] tracking-wide">
                    {t("dateTime.transferDate")}
                  </span>
                  <span className="font-bold text-foreground">
                    {summaryDetails.date}
                  </span>
                </div>
              </div>

              {/* Status Indicator inside Summary */}
              {hasErrors ? (
                <div className="p-3 bg-rose-50 rounded-xl border border-rose-100 flex items-start gap-2 text-[11px] text-rose-800 font-bold leading-normal">
                  <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5 text-rose-600" />
                  <div>
                    <span>{t("validation.validationErrors")}</span>
                    <ul className="list-disc pl-4 mt-1 font-semibold space-y-0.5 text-[10px]">
                      {Object.values(errors).map((err, idx) => (
                        <li key={idx}>{err}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              ) : (
                <div className="p-3 bg-emerald-50 rounded-xl border border-emerald-100 flex items-center gap-2 text-[11px] text-emerald-800 font-bold">
                  <CheckCircle className="h-4 w-4 shrink-0 text-emerald-600" />
                  <span>{t("inventory.readyToTransfer")}</span>
                </div>
              )}
            </ContentCard>
          </div>
        </div>
      )}
    </AppLayout>
  );
}
